<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4><i class="fal fa-chart-bar"></i> ລາຍງານຄຳຮ້ອງຊົດເຊີຍ</h4>
      <button class="button is-dark" @click="$router.push({ name: 'claim.index' })">ກັບໄປຄິວ</button>
    </div>

    <!--
      The period here is REAL, unlike the queue's — the report endpoints take
      `from`/`to` and filter server-side.
    -->
    <div class="box">
      <div class="claim-filter">
        <div class="claim-filter__field">
          <label class="label">ຈາກວັນທີ:</label>
          <input class="input" type="date" v-model="from">
        </div>
        <div class="claim-filter__field">
          <label class="label">ຫາວັນທີ:</label>
          <input class="input" type="date" v-model="to">
        </div>
        <div class="claim-filter__actions">
          <button class="button is-primary" :disabled="loading" @click="fetchAll">
            {{ loading ? 'ກຳລັງໂຫລດ...' : 'ໂຫລດລາຍງານ' }}
          </button>
          <!--
            The SAME window the screen is showing. The server builds the file from the screen's
            own query, so the two cannot report different totals — which is the whole reason the
            dates are passed rather than the file being "everything".
          -->
          <button class="button" :disabled="loading || exporting" @click="exportExcel">
            {{ exporting ? 'ກຳລັງສ້າງໄຟລ໌...' : 'ດາວໂຫລດ Excel' }}
          </button>
        </div>
      </div>
    </div>

    <div class="box claim-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="button"
        :class="{ 'is-primary': active === tab.id }"
        @click="active = tab.id"
      >
        {{ tab.name }}
      </button>
    </div>

    <!-- ── summary ──────────────────────────────────────────────────── -->
    <div v-if="active === 'summary' && summary">
      <div class="claim-cards">
        <div class="claim-card">
          <span class="claim-card__label">ຄຳຮ້ອງທັງໝົດ</span>
          <strong>{{ summary.total_claims }}</strong>
        </div>
        <!--
          The first number to look at when the budget runs dry: money the ERP has
          reserved that nobody has signed for.
        -->
        <div class="claim-card is-warn">
          <span class="claim-card__label">ຄ້າງຍ້ອນງົບ</span>
          <strong>{{ summary.open.waiting_budget }}</strong>
        </div>
        <!-- Approved by a person, not yet paid by finance. Not a failure. -->
        <div class="claim-card">
          <span class="claim-card__label">ອະນຸມັດແລ້ວ ຍັງບໍ່ໂອນ</span>
          <strong>{{ summary.open.approved_unpaid }}</strong>
        </div>
      </div>

      <div class="columns is-multiline">
        <div class="column is-6">
          <div class="box">
            <h5 class="claim-section">ຕາມຜູ້ຮັບຜິດຊອບ</h5>
            <table class="table is-fullwidth">
              <thead><tr><th></th><th>ຈຳນວນ</th><th>ມູນຄ່າ</th></tr></thead>
              <tbody>
                <!--
                  All five rows, always — four parties plus the claims assessed before the party
                  was recorded. A party missing from the period reads as a zero here rather than
                  as a row somebody forgot to add.
                -->
                <tr v-for="key in ['company', 'sorting_centre', 'branch', 'vehicle_line', 'unassessed']" :key="key">
                  <th>{{ partyLabel(key) }}</th>
                  <td>{{ summary.by_liable_party[key].claims }}</td>
                  <td>{{ formatMoney(summary.by_liable_party[key].amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="column is-6">
          <div class="box">
            <h5 class="claim-section">ຕາມປະເພດ ແລະ ການປະຕິເສດ</h5>
            <table class="table is-fullwidth">
              <tbody>
                <tr v-for="key in ['lost', 'damaged']" :key="key">
                  <th>{{ kindLabel(key) }}</th>
                  <td>{{ summary.by_kind[key].claims }}</td>
                  <td>{{ formatMoney(summary.by_kind[key].amount) }}</td>
                </tr>
                <!--
                  Two different things. A claim WE refused is a decision of ours;
                  one the ERP refused is a decision of theirs, and the customer
                  is told something different in each case.
                -->
                <tr><th>ປະຕິເສດໂດຍພວກເຮົາ</th><td colspan="2">{{ summary.rejected.by_us }}</td></tr>
                <tr><th>ປະຕິເສດໂດຍ ERP</th><td colspan="2">{{ summary.rejected.by_erp }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Task 5.5: old cases are still real cases while they drain. -->
      <div class="notification-line is-info" v-if="summary.legacy_injured_parcels">
        ໃນຊ່ວງເວລານີ້ຍັງມີ {{ summary.legacy_injured_parcels }} ລາຍການໃນລະບົບເກົ່າ (injured parcel)
        ທີ່ບໍ່ໄດ້ນັບຢູ່ໃນຕົວເລກຂ້າງເທິງ — ເບິ່ງໄດ້ໃນໜ້າລະບົບເກົ່າ
      </div>
    </div>

    <!-- ── rows ─────────────────────────────────────────────────────── -->
    <div v-if="active === 'rows'">
      <div class="box">
        <h5 class="claim-section">ລາຍການຄຳຮ້ອງ</h5>
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th>ເລກຄຳຮ້ອງ</th><th>ເລກບິນ</th><th>ປະເພດ</th><th>ສະຖານະ</th>
              <th>ຜູ້ຮັບຜິດຊອບ</th><th>ລູກຄ້າຂໍ</th><th>ອະນຸມັດ</th><th>ເອກະສານ ERP</th><th>ໂອນເມື່ອ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.claim_no">
              <td>{{ row.claim_no }}</td>
              <td>{{ row.shipment_number }}</td>
              <td>{{ kindLabel(row.kind) }}</td>
              <td>{{ statusLabel(row.status) }}</td>
              <td>{{ partyLabel(row.liable_party_kind) }}</td>
              <td>{{ formatMoney(row.claimed_amount) }}</td>
              <td>{{ formatMoney(row.amount) }}</td>
              <td>{{ row.erp_doc_no || '-' }}</td>
              <td>{{ row.settled_at || '-' }}</td>
            </tr>
            <tr v-if="!rows.length"><td colspan="9">ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້</td></tr>
          </tbody>
        </table>

        <!--
          Task 5.2/5.4: capped at 500 SERVER-side. Paged, and never offered as a
          "download everything" — a button that silently returns the first 500
          rows of a year is a reconciliation that quietly does not balance.
        -->
        <div class="claim-pager" v-if="rowsMeta.last_page > 1">
          <button class="button" :disabled="rowsPage <= 1" @click="goRows(rowsPage - 1)">ກ່ອນໜ້າ</button>
          <span>ໜ້າ {{ rowsMeta.current_page }} / {{ rowsMeta.last_page }}</span>
          <button class="button" :disabled="rowsPage >= rowsMeta.last_page" @click="goRows(rowsPage + 1)">
            ຖັດໄປ
          </button>
        </div>
        <p class="claim-note">
          ລາຍງານນີ້ຈຳກັດ 500 ແຖວຕໍ່ການດຶງໜຶ່ງຄັ້ງ ຢູ່ຝັ່ງເຊີບເວີ — ໃຊ້ການເລື່ອນໜ້າ
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ClaimApi from '@/Mixins/Claim/ClaimApi';

/**
 * The two reports, in one screen with a shared period.
 *
 * Written for the people who reconcile: the summary answers "where is the money
 * stuck" and the rows report is the line-by-line behind it.
 *
 * There is no branch-liability report any more. The company pays every claimant
 * through the ERP, and what it then recovers from a liable branch or a vehicle
 * line is the successor document's business, in the ERP's own books — reporting
 * it from here would be a second set of figures for money this system never
 * sees move.
 */
export default {
  name: 'ClaimReport',
  mixins: [ClaimApi],

  data: () => ({
    exporting: false,
    from: moment().startOf('month').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
    active: 'summary',
    loading: false,

    summary: null,
    rows: [],
    rowsMeta: {},
    rowsPage: 1,

    tabs: [
      { id: 'summary', name: 'ພາບລວມ' },
      { id: 'rows', name: 'ລາຍການ' },
    ],
  }),

  created() {
    this.$store.dispatch('Claim/load');
    this.fetchAll();
  },

  methods: {
    /**
     * Download the window the screen is showing.
     *
     * A BLOB, not a link: `/api/claims/reports/export` is behind `auth:api`, and an
     * `<a href>` or `window.open` sends no Authorization header — it would answer 401 and the
     * browser would show a blank tab rather than an error anybody can act on.
     */
    exportExcel() {
      this.exporting = true;

      return this.claimReportExport({ from: this.from, to: this.to })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');

          link.href = url;
          link.setAttribute('download', `claim-report_${this.from}_${this.to}.xlsx`);
          document.body.appendChild(link);
          link.click();
          // Both released: the node and the object URL, or a long session leaks one of each
          // per download.
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => this.handleActionError(error))
        .finally(() => { this.exporting = false; });
    },

    statusLabel(value) {
      return this.$store.getters['Claim/statusLabel'](value);
    },

    optionLabel(group, value) {
      const found = this.$store.getters['Claim/options'](group).find((o) => o.value === value);
      return found ? found.label : value;
    },

    kindLabel(value) {
      return this.optionLabel('claim_kind', value);
    },

    partyLabel(value) {
      // `unassessed` is this report's own bucket, not a published enumeration — a claim with no
      // party decided yet is not a fifth kind of party. A blank reads the same way: a row assessed
      // before the party was recorded says nothing about who carried it.
      if (!value || value === 'unassessed') return 'ຍັງບໍ່ທັນປະເມີນ';

      return this.optionLabel('liable_party_kind', value);
    },

    fetchAll() {
      this.loading = true;
      this.rowsPage = 1;

      const params = { from: this.from, to: this.to };

      return Promise.all([
        this.claimReportSummary(params).then((res) => {
          this.summary = res.data.data;
        }),
        this.fetchRows(1),
      ])
        .catch((error) => this.handleActionError(error))
        .finally(() => {
          this.loading = false;
        });
    },

    fetchRows(page) {
      return this.claimReportRows({ from: this.from, to: this.to, page, per_page: 100 }).then((res) => {
        const body = res.data.data;
        this.rows = body.data || [];
        this.rowsMeta = body;
        this.rowsPage = page;
      });
    },

    goRows(page) {
      this.fetchRows(page).catch((error) => this.handleActionError(error));
    },
  },
};
</script>

<style lang="scss" scoped>
.claim-section {
  font-weight: 700;
  margin-bottom: 10px;
}

.claim-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;

  &__field { min-width: 180px; }

  /**
   * The two buttons sat edge to edge and read as one control with a seam down it.
   * `.claim-filter`'s own `gap` only separates its CHILDREN — this wrapper is one of them, so
   * what is inside it needs its own.
   */
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
}

.claim-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.claim-cards {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  .claim-card {
    background: #fff;
    border-radius: 6px;
    padding: 12px 16px;
    min-width: 150px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);

    &__label { display: block; font-size: 12px; color: #7a7a7a; }
    strong { font-size: 22px; }

    &.is-warn { background: #fdf6e3; }
  }
}

.notification-line {
  padding: 10px 14px;
  border-radius: 4px;
  margin-bottom: 10px;

  &.is-info { background: #eaf4fd; color: #1f5f8b; }
}

.claim-pager {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.claim-note {
  font-size: 12px;
  color: #7a7a7a;
  margin-top: 8px;
}
</style>
